import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';

import env from 'Env';
import FluidImg from 'Components/Utility/FluidImg';
import ScreenshotCarousel from 'Components/Cards/ScreenshotCarousel';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import imageSizes from 'Components/Cards/image-sizes';

const MediaPreview = ({
  cover,
  name,
  sizesOverride,
  isHovering,
  fullPrice,
  type,
  displayType,
  gameTotal,
  videoClipPoster,
  videoClipFiles,
  slideNumber,
  cardSize,
  slides,
  ageRatings,
  disableVideo,
}) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef();
  const fallbackVideoRef = useRef();
  const { ref, inView } = useInView({ triggerOnce: true });
  const isChome = navigator.userAgent.indexOf('Chrome') !== -1 || window.chrome;

  const cardVideosExist = videoClipFiles?.length > 0;
  const videoCdn = env.videoCdnUrl;

  // Memoized so that videoclips are not recalculated and passed to video component on every render
  const videoClips = useMemo(() => ({
    large: videoClipFiles
      .filter(v => v.resolution === '480p')
      .sort((a, b) => (a.fileType > b.fileType ? -1 : 1)),
    medium: videoClipFiles
      .filter(v => v.resolution === '360p')
      .sort((a, b) => (a.fileType > b.fileType ? -1 : 1)),
    small: videoClipFiles
      .filter(v => v.resolution === '240p')
      .sort((a, b) => (a.fileType > b.fileType ? -1 : 1)),
  }), [videoClipFiles]);

  const onLoadedData = () => {
    setVideoLoaded(true);
  };

  const onCanPlay = () => {
    setVideoLoaded(true);
    if (fallbackVideoRef.current) {
      fallbackVideoRef.current.play();
    }
  };

  const showAgeGate = type === 'bundle' ? false : ProductRequirements.showAgeGate(ageRatings);

  const OptimisedVideoElement = () => (
    <video
      muted
      loop
      autoPlay={isHovering}
      poster={`${videoClipPoster ? `${videoCdn}${videoClipPoster}` : ''}`}
      className={`cover-video ${isHovering && videoLoaded ? 'd-block' : 'd-none'}`}
      ref={videoRef}
      preload={inView ? 'auto' : 'metadata'}
      onLoadedData={onLoadedData}
    >
      {cardSize === 'large' && videoClips.large.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          media="(min-width:1520px)"
          key={clip.path + clip.fileType}
        />
      ))}
      {cardSize === 'medium' && videoClips.medium.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          media="(min-width:1200px)"
          key={clip.path + clip.fileType}
        />
      ))}
      {cardSize === 'small' && videoClips.small.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          key={clip.path + clip.fileType}
        />
      ))}
    </video>
  );


  const VideoElement = () => (
    <video
      muted
      loop
      autoPlay
      playsInline
      className="cover-video"
      ref={fallbackVideoRef}
      onCanPlay={onCanPlay}
    >
      {cardSize === 'large' && videoClips.large.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          media="(min-width:1520px)"
          key={clip.path + clip.fileType}
        />
      ))}
      {cardSize === 'medium' && videoClips.medium.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          media="(min-width:1200px)"
          key={clip.path + clip.fileType}
        />
      ))}
      {cardSize === 'small' && videoClips.small.map(clip => (
        <source
          src={`${videoCdn}${clip.path}`}
          type={`video/${clip.fileType}`}
          key={clip.path + clip.fileType}
        />
      ))}
    </video>
  );
  const LoadingSpinner = () => (
    <div className="infinite-loader" id="infinite-loader">
      <div className="spinner">
        <div className="rect1" />
        <div className="rect2" />
        <div className="rect3" />
        <div className="rect4" />
        <div className="rect5" />
      </div>
    </div>
  );

  return (
    <div ref={ref} className={`MediaPreview ${cardVideosExist ? 'w-100 d-block' : ''}`}>
      {(!cardVideosExist || disableVideo) && (
        <React.Fragment>
          {!showAgeGate && isHovering && slides.length > 1 ? (
            <ScreenshotCarousel
              screenshots={slides}
              fullPrice={fullPrice}
              type={type}
              displayType={displayType}
              gameTotal={gameTotal}
              slideNumber={slideNumber}
              cardSize={cardSize}
              isHovering={isHovering}
            />
          ) : (

            <div className="responsive-image-island cover-image-container">
              <div className="responsive-image responsive-image--16by9">
                <FluidImg
                  ratio16by9
                  sizesOverride={sizesOverride}
                  className="img-force-full"
                  src={cover}
                  alt={name}
                />
              </div>
            </div>
          )}
        </React.Fragment>
      )}

      {cardVideosExist && !disableVideo &&
        <div className="responsive-image-island cover-video-container">
          <div className="responsive-image responsive-image--16by9">
            <FluidImg
              ratio16by9
              sizesOverride={sizesOverride}
              className="img-force-full"
              src={cover}
              alt={name}
            />
            {inView && isHovering && !videoLoaded && <LoadingSpinner />}
            {isChome && <OptimisedVideoElement /> }
            {!isChome && isHovering && <VideoElement />}
          </div>
        </div>
      }
    </div>
  );
};

MediaPreview.propTypes = {
  cover: PropTypes.string,
  name: PropTypes.string,
  sizesOverride: PropTypes.string,
  isHovering: PropTypes.bool,
  fullPrice: PropTypes.shape({}),
  type: PropTypes.string,
  displayType: PropTypes.string,
  gameTotal: PropTypes.number,
  videoClipPoster: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  slideNumber: PropTypes.number,
  videoClipFiles: PropTypes.arrayOf(PropTypes.object),
  slides: PropTypes.arrayOf(PropTypes.string),
  cardSize: PropTypes.string,
  ageRatings: PropTypes.shape({}),
  disableVideo: PropTypes.bool,
};

MediaPreview.defaultProps = {
  name: '',
  cover: '',
  sizesOverride: imageSizes(3, 4, 4, 6),
  isHovering: false,
  fullPrice: {},
  type: 'game',
  displayType: 'game',
  gameTotal: 0,
  videoClipPoster: '',
  slideNumber: 0,
  videoClipFiles: [],
  cardSize: 'small',
  slides: [],
  ageRatings: {},
  disableVideo: false,
};

export default MediaPreview;
